import { useState, useCallback } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Button } from "../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { RefreshCw, Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { ConfigFieldsProps } from "./types";
import { fetchSheetData } from "../../../services/google-sheets.service";
import { useIntegrationsStore } from "../../../stores/integrations.store";
import { useWorkspaceFilterGroupsStore } from "../../../stores/workspace-filter-groups.store";

type RawFilterOption = { label: string; value: string; values?: string[] };
type Group = { id: string; name: string; values: string[] };

function makeId() {
  return Math.random().toString(36).slice(2, 9);
}

function groupsToOptions(groups: Group[]): RawFilterOption[] {
  return groups
    .filter((g) => g.name.trim())
    .map((g) => ({ label: g.name, value: g.name, values: g.values }));
}

export function FilterConfig({
  editedComponent,
  updateConfig,
  availableComponents,
}: ConfigFieldsProps) {
  const { getById: getIntegrationById } = useIntegrationsStore();
  const { filterGroups } = useWorkspaceFilterGroupsStore();
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  // Raw values from the column (all unique values from the data source)
  const [rawValues, setRawValues] = useState<string[]>(() => {
    const opts: RawFilterOption[] = editedComponent.config?.options || [];
    if (editedComponent.config?.enableManualGrouping) {
      return [...new Set(opts.flatMap((o) => o.values?.length ? o.values : [o.value]))];
    }
    return opts.map((o) => (typeof o === "string" ? o : o.value));
  });

  // Groups state (only used in manual grouping mode)
  const [groups, setGroups] = useState<Group[]>(() => {
    const opts: RawFilterOption[] = editedComponent.config?.options || [];
    if (editedComponent.config?.enableManualGrouping && opts.length > 0) {
      return opts.map((opt) => ({
        id: makeId(),
        name: opt.label,
        values: opt.values || [opt.value],
      }));
    }
    return [];
  });

  const filterPreset = editedComponent.config.preset || "custom";
  const optionsSource = editedComponent.config.optionsSource || "manual";
  const sourceTableId = editedComponent.config.sourceTableId || "";
  const enableManualGrouping = editedComponent.config?.enableManualGrouping ?? false;

  const presetNames: Record<string, string> = {
    monthly: "Mensal (12 meses)",
    semester: "Semestral (2 semestres)",
    quarterly: "Trimestral (4 trimestres)",
    custom: "Personalizado",
  };

  const tableComponents = availableComponents.filter((c) => c.type === "table");
  const selectedTable = tableComponents.find((c) => c.id === sourceTableId);
  const tableColumns: any[] = selectedTable?.config?.columns || [];

  const resolveTableRows = useCallback(
    async (table: any): Promise<any[]> => {
      if (!table) return [];

      let rows: any[] = [];

      if ((table.config?.dataSource?.data || []).length > 0) {
        rows = table.config.dataSource.data;
      }

      if (rows.length === 0 && table.config?.googleSheets?.enabled) {
        const spreadsheetId = table.config.googleSheets.integrationId
          ? (getIntegrationById(
              table.config.googleSheets.integrationId,
            )?.config?.spreadsheetId as string | undefined)
          : table.config.googleSheets.spreadsheetId;

        if (spreadsheetId) {
          const range = table.config.googleSheets.range || "Sheet1";
          const { rows: sheetRows } = await fetchSheetData(spreadsheetId, range);
          rows = sheetRows;
        }
      }

      return rows;
    },
    [getIntegrationById],
  );

  const extractUniqueValues = useCallback(
    async (columnField: string): Promise<{ label: string; value: string }[]> => {
      if (!selectedTable) return [];

      const rows = await resolveTableRows(selectedTable);

      const columnDef = tableColumns.find((c: any) => c.field === columnField);
      const isTableLink = columnDef?.type === "tableLink";

      const rawValues = rows
        .map((r: any) => r[columnField])
        .filter((v: any) => v !== null && v !== undefined && v !== "");

      if (isTableLink) {
        // Colunas tableLink guardam um objeto { name, sheetsUrl, pageSlug };
        // o nome da página cadastrada é o valor que deve virar opção do filtro.
        const names = [
          ...new Set(
            rawValues
              .map((v: any) => (typeof v === "object" ? v.name : String(v)))
              .filter((name: any) => !!name),
          ),
        ] as string[];
        return names.map((name) => ({ label: name, value: name }));
      }

      const unique = [...new Set(rawValues)] as any[];

      return unique.map((v) => ({ label: String(v), value: String(v) }));
    },
    [selectedTable, tableColumns, resolveTableRows],
  );

  // Modo "Tabela Vinculada": cada linha com uma coluna tableLink aponta para uma
  // página filha; a opção do filtro passa a ser o ID do componente table dessa
  // página (não um valor de coluna), para permitir trocar a fonte de outros widgets.
  const extractLinkedTableOptions = useCallback(
    async (columnField: string): Promise<{ label: string; value: string }[]> => {
      if (!selectedTable) return [];

      const rows = await resolveTableRows(selectedTable);
      const results: { label: string; value: string }[] = [];

      for (const row of rows) {
        const linkVal = row[columnField];
        if (!linkVal || typeof linkVal !== "object" || !linkVal.pageSlug) continue;

        const childTable = availableComponents.find(
          (c: any) => c.type === "table" && c._pageSlug === linkVal.pageSlug,
        );
        if (!childTable) continue;

        results.push({ label: linkVal.name || childTable.id, value: childTable.id });
      }

      return results;
    },
    [selectedTable, availableComponents, resolveTableRows],
  );

  const handleColumnSelect = async (columnField: string) => {
    updateConfig("field", columnField);
    setOptionsError(null);

    if (!columnField) {
      updateConfig("options", []);
      setRawValues([]);
      return;
    }

    setLoadingOptions(true);
    try {
      const rawOptions =
        optionsSource === "linkedTable"
          ? await extractLinkedTableOptions(columnField)
          : await extractUniqueValues(columnField);
      const newRawValues = rawOptions.map((o) => o.value);
      setRawValues(newRawValues);

      if (enableManualGrouping) {
        // Column changed: reset groups, keep raw values for the user to assign
        setGroups([]);
        updateConfig("options", []);
      } else {
        updateConfig("options", rawOptions);
      }
    } catch (e: any) {
      setOptionsError("Não foi possível carregar os valores. Verifique a integração.");
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleRefreshOptions = async () => {
    const field = editedComponent.config.field;
    if (!field) return;
    setOptionsError(null);
    setLoadingOptions(true);
    try {
      const rawOptions =
        optionsSource === "linkedTable"
          ? await extractLinkedTableOptions(field)
          : await extractUniqueValues(field);
      const newRawValues = rawOptions.map((o) => o.value);
      setRawValues(newRawValues);

      if (enableManualGrouping) {
        // Keep existing groups, just remove values that no longer exist in data
        setGroups((prev) => {
          const updated = prev.map((g) => ({
            ...g,
            values: g.values.filter((v) => newRawValues.includes(v)),
          }));
          updateConfig("options", groupsToOptions(updated));
          return updated;
        });
      } else {
        updateConfig("options", rawOptions);
      }
    } catch (e: any) {
      setOptionsError("Não foi possível atualizar os valores.");
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleToggleManualGrouping = (enabled: boolean) => {
    updateConfig("enableManualGrouping", enabled);
    if (enabled) {
      // Start with no groups — user creates them manually
      setGroups([]);
      updateConfig("options", []);
    } else {
      // Revert to flat individual options
      setGroups([]);
      const flatOptions = rawValues.map((v) => ({ label: v, value: v }));
      updateConfig("options", flatOptions);
    }
  };

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const toggleCollapse = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleAddGroup = () => {
    const id = makeId();
    setGroups((prev) => [...prev, { id, name: "", values: [] }]);
    // Novos grupos começam expandidos
    setCollapsedGroups((prev) => { const n = new Set(prev); n.delete(id); return n; });
  };

  const handleDeleteGroup = (groupId: string) => {
    setGroups((prev) => {
      const updated = prev.filter((g) => g.id !== groupId);
      updateConfig("options", groupsToOptions(updated));
      return updated;
    });
  };

  const handleRenameGroup = (groupId: string, name: string) => {
    setGroups((prev) => {
      const updated = prev.map((g) => (g.id === groupId ? { ...g, name } : g));
      updateConfig("options", groupsToOptions(updated));
      return updated;
    });
  };

  const handleToggleValue = (groupId: string, rawValue: string, checked: boolean) => {
    setGroups((prev) => {
      const updated = prev.map((g) => {
        if (checked) {
          // Add to this group, remove from all other groups (exclusive assignment)
          if (g.id === groupId) {
            return { ...g, values: [...g.values.filter((v) => v !== rawValue), rawValue] };
          } else {
            return { ...g, values: g.values.filter((v) => v !== rawValue) };
          }
        } else {
          if (g.id === groupId) {
            return { ...g, values: g.values.filter((v) => v !== rawValue) };
          }
          return g;
        }
      });
      updateConfig("options", groupsToOptions(updated));
      return updated;
    });
  };

  const savedOptions: RawFilterOption[] = editedComponent.config.options || [];

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="filterTitle">Título</Label>
        <Input
          id="filterTitle"
          type="text"
          value={editedComponent.config.title || ""}
          onChange={(e) => updateConfig("title", e.target.value)}
          placeholder="Ex: Filtro por Obra"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="filterType">Tipo de Filtro</Label>
        <Select
          value={filterPreset}
          onValueChange={(value) => updateConfig("preset", value)}
        >
          <SelectTrigger id="filterType">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="custom">Customizado</SelectItem>
            <SelectItem value="monthly">Mensal (12 meses)</SelectItem>
            <SelectItem value="semester">Semestral (2 semestres)</SelectItem>
            <SelectItem value="quarterly">Trimestral (4 trimestres)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filterPreset !== "custom" && (
        <>
          <div className="p-4 bg-accent border border-primary/20 rounded-md">
            <p className="text-sm mb-2">
              <strong>Filtro {presetNames[filterPreset]}</strong>
            </p>
            <p className="text-xs text-muted-foreground">
              {filterPreset === "monthly" &&
                'Filtra dados pela coluna "data" extraindo o mês (01-12)'}
              {filterPreset === "semester" &&
                'Filtra dados pela coluna "data" extraindo o semestre (1-2)'}
              {filterPreset === "quarterly" &&
                'Filtra dados pela coluna "data" extraindo o trimestre (1-4)'}
            </p>
          </div>

          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="enableGrouping"
              checked={editedComponent.config?.enableGrouping ?? false}
              onChange={(e) => updateConfig("enableGrouping", e.target.checked)}
              className="mt-1 w-4 h-4 rounded"
            />
            <div>
              <Label htmlFor="enableGrouping" className="text-sm cursor-pointer">
                Agrupar dados por período
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Quando ativo, os gráficos irão agrupar e somar valores do mesmo
                período. Se desativado, mostra todos os registros individuais.
              </p>
            </div>
          </div>
        </>
      )}

      {filterPreset === "custom" && (
        <>
          <div className="space-y-2">
            <Label>Fonte das Opções</Label>
            <Select
              value={optionsSource}
              onValueChange={(value) => {
                updateConfig("optionsSource", value);
                updateConfig("sharedFilterGroupId", "");
                if (value === "table") {
                  updateConfig("options", []);
                } else if (value === "linkedTable") {
                  updateConfig("options", []);
                  updateConfig("enableManualGrouping", false);
                  setGroups([]);
                  setRawValues([]);
                } else if (value === "shared") {
                  updateConfig("sourceTableId", "");
                  updateConfig("options", []);
                  updateConfig("enableManualGrouping", false);
                  setGroups([]);
                  setRawValues([]);
                } else {
                  updateConfig("sourceTableId", "");
                  updateConfig("field", "");
                  updateConfig("options", []);
                  updateConfig("enableManualGrouping", false);
                  setGroups([]);
                  setRawValues([]);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual (digitar valores)</SelectItem>
                <SelectItem value="table">Por Tabela</SelectItem>
                <SelectItem value="linkedTable">Tabela Vinculada</SelectItem>
                <SelectItem value="shared">Grupos Manuais (workspace)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {optionsSource === "shared" ? (
            <div className="space-y-2">
              <Label>Grupo de Filtros</Label>
              <Select
                value={editedComponent.config.sharedFilterGroupId || ""}
                onValueChange={(val) => {
                  updateConfig("sharedFilterGroupId", val);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um grupo de filtros" />
                </SelectTrigger>
                <SelectContent>
                  {filterGroups.length === 0 ? (
                    <SelectItem value="_empty" disabled>
                      Nenhum grupo criado
                    </SelectItem>
                  ) : (
                    filterGroups.map((fg) => (
                      <SelectItem key={fg.id} value={fg.id}>
                        {fg.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {editedComponent.config.sharedFilterGroupId && (() => {
                const fg = filterGroups.find((g) => g.id === editedComponent.config.sharedFilterGroupId);
                return fg ? (
                  <div className="flex flex-wrap gap-1 p-2 bg-muted/40 rounded border text-xs">
                    {fg.groups.length === 0
                      ? <span className="text-muted-foreground">Nenhum grupo configurado ainda.</span>
                      : fg.groups.map((g) => (
                          <span key={g.id} className="bg-background border rounded px-1.5 py-0.5">
                            {g.name}
                          </span>
                        ))}
                  </div>
                ) : null;
              })()}
              <p className="text-xs text-muted-foreground">
                Gerencie os grupos em <strong>Grupos de Filtros</strong> na barra lateral.
              </p>
            </div>
          ) : optionsSource === "table" || optionsSource === "linkedTable" ? (
            <>
              <div className="space-y-2">
                <Label>Tabela</Label>
                <Select
                  value={sourceTableId}
                  onValueChange={(value) => {
                    updateConfig("sourceTableId", value);
                    updateConfig("field", "");
                    updateConfig("options", []);
                    updateConfig("enableManualGrouping", false);
                    setGroups([]);
                    setRawValues([]);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma tabela" />
                  </SelectTrigger>
                  <SelectContent>
                    {tableComponents.length === 0 ? (
                      <SelectItem value="_empty" disabled>
                        Nenhuma tabela disponível
                      </SelectItem>
                    ) : (
                      tableComponents.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.config?.title || t.id}
                          {(t as any)._pageName ? ` — ${(t as any)._pageName}` : ""}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {sourceTableId && (
                <div className="space-y-2">
                  <Label>Coluna</Label>
                  <Select
                    value={editedComponent.config.field || ""}
                    onValueChange={handleColumnSelect}
                    disabled={loadingOptions}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma coluna" />
                    </SelectTrigger>
                    <SelectContent>
                      {(optionsSource === "linkedTable"
                        ? tableColumns.filter((c: any) => c.type === "tableLink")
                        : tableColumns
                      ).map((col: any) => (
                        <SelectItem key={col.field} value={col.field}>
                          {col.label || col.field}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {optionsSource === "linkedTable" &&
                    tableColumns.filter((c: any) => c.type === "tableLink").length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Essa tabela não tem nenhuma coluna do tipo "Link para Planilha".
                      </p>
                    )}
                </div>
              )}

              {editedComponent.config.field && (
                <>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">
                      {loadingOptions
                        ? "Carregando valores..."
                        : `${rawValues.length} valor(es) disponível(is)`}
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRefreshOptions}
                      disabled={loadingOptions}
                      className="h-7 px-2 text-xs gap-1"
                    >
                      <RefreshCw className={`w-3 h-3 ${loadingOptions ? "animate-spin" : ""}`} />
                      Atualizar
                    </Button>
                  </div>

                  {optionsError && (
                    <p className="text-xs text-destructive">{optionsError}</p>
                  )}

                  {/* Toggle agrupamento manual (não se aplica ao modo Tabela Vinculada) */}
                  {optionsSource === "table" && (
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="enableManualGrouping"
                        checked={enableManualGrouping}
                        onChange={(e) => handleToggleManualGrouping(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded"
                      />
                      <div>
                        <Label htmlFor="enableManualGrouping" className="text-sm cursor-pointer">
                          Criar agrupamentos manuais
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Crie grupos e marque quais valores pertencem a cada um.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* UI de agrupamento manual */}
                  {enableManualGrouping ? (
                    <div className="space-y-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddGroup}
                        className="w-full gap-2 text-xs"
                      >
                        <Plus className="w-3 h-3" />
                        Adicionar Grupo
                      </Button>

                      {groups.length === 0 && (
                        <p className="text-xs text-muted-foreground text-center py-2">
                          {rawValues.length === 0
                            ? "Atualize os valores para começar."
                            : "Clique em \"Adicionar Grupo\" para criar o primeiro grupo."}
                        </p>
                      )}

                      {groups.map((group) => {
                        const isCollapsed = collapsedGroups.has(group.id);
                        return (
                          <div key={group.id} className="border rounded-md overflow-hidden">
                            {/* Cabeçalho do grupo */}
                            <div className="flex items-center gap-1 px-2 py-2 bg-muted/50">
                              <button
                                type="button"
                                onClick={() => toggleCollapse(group.id)}
                                className="shrink-0 text-muted-foreground hover:text-foreground"
                              >
                                {isCollapsed
                                  ? <ChevronRight className="w-4 h-4" />
                                  : <ChevronDown className="w-4 h-4" />}
                              </button>
                              <Input
                                value={group.name}
                                onChange={(e) => handleRenameGroup(group.id, e.target.value)}
                                placeholder="Nome do grupo"
                                className="h-7 text-sm font-medium flex-1"
                              />
                              {isCollapsed && group.values.length > 0 && (
                                <span className="text-xs text-muted-foreground shrink-0 px-1">
                                  {group.values.length} valor(es)
                                </span>
                              )}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteGroup(group.id)}
                                className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive shrink-0"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>

                            {/* Lista de valores com checkboxes (recolhível) */}
                            {!isCollapsed && (
                              <>
                                <div className="max-h-48 overflow-y-auto divide-y border-t">
                                  {rawValues.length === 0 ? (
                                    <p className="text-xs text-muted-foreground px-3 py-2">
                                      Nenhum valor carregado. Use "Atualizar".
                                    </p>
                                  ) : (
                                    rawValues.map((rawValue) => {
                                      const isChecked = group.values.includes(rawValue);
                                      const isInOtherGroup = !isChecked &&
                                        groups.some((g) => g.id !== group.id && g.values.includes(rawValue));
                                      return (
                                        <label
                                          key={rawValue}
                                          className="flex items-center gap-2.5 px-3 py-1.5 cursor-pointer hover:bg-muted/30 select-none"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={(e) =>
                                              handleToggleValue(group.id, rawValue, e.target.checked)
                                            }
                                            className="w-3.5 h-3.5 shrink-0"
                                          />
                                          <span
                                            className={`text-xs truncate ${isInOtherGroup ? "text-muted-foreground" : ""}`}
                                            title={rawValue}
                                          >
                                            {rawValue}
                                          </span>
                                          {isInOtherGroup && (
                                            <span className="ml-auto text-xs text-muted-foreground/60 shrink-0">
                                              outro grupo
                                            </span>
                                          )}
                                        </label>
                                      );
                                    })
                                  )}
                                </div>

                                {group.values.length > 0 && (
                                  <div className="px-3 py-1.5 bg-muted/30 border-t">
                                    <p className="text-xs text-muted-foreground">
                                      {group.values.length} valor(es) selecionado(s)
                                    </p>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    /* Visualização normal (sem agrupamento) */
                    savedOptions.length > 0 && (
                      <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto p-2 bg-muted/40 rounded border text-xs">
                        {savedOptions.map((o) => (
                          <span
                            key={o.value}
                            className="bg-background border rounded px-1.5 py-0.5"
                          >
                            {o.label}
                          </span>
                        ))}
                      </div>
                    )
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="filterField">Campo para Filtrar</Label>
                <Input
                  id="filterField"
                  type="text"
                  value={editedComponent.config.field || ""}
                  onChange={(e) => updateConfig("field", e.target.value)}
                  placeholder="Ex: categoria, status, tipo"
                />
                <p className="text-xs text-muted-foreground">
                  Nome da coluna nos seus dados para filtrar
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="filterOptions">Opções (uma por linha)</Label>
                <Textarea
                  id="filterOptions"
                  value={(editedComponent.config.options || [])
                    .map((o: any) =>
                      typeof o === "string" ? o : o.value ?? o.label ?? "",
                    )
                    .join("\n")}
                  onChange={(e) =>
                    updateConfig(
                      "options",
                      e.target.value
                        .split("\n")
                        .filter((o: string) => o.trim()),
                    )
                  }
                  placeholder="Opção 1\nOpção 2\nOpção 3"
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  Lista de valores que aparecerão no dropdown
                </p>
              </div>
            </>
          )}
        </>
      )}

      <div className="p-4 bg-accent border border-primary/20 rounded-md">
        <p className="text-sm">
          <strong>💡 Dica:</strong>{" "}
          {optionsSource === "linkedTable" && filterPreset === "custom"
            ? "Cada opção corresponde à página vinculada daquela linha. Use este filtro para trocar a fonte de dados de métricas e gráficos configurados com \"Dinâmica (via filtro)\"."
            : optionsSource === "table" && filterPreset === "custom"
            ? enableManualGrouping
              ? "Um valor só pode pertencer a um grupo. Marcar em um grupo desmarca automaticamente nos outros."
              : "As opções são salvas no filtro. Use o botão Atualizar para sincronizar quando os dados mudarem."
            : "Certifique-se de que seus dados (Excel/Tabela) tenham uma coluna com o nome correspondente ao filtro escolhido."}
        </p>
      </div>
    </>
  );
}
